// Pax Magellanic — Flight HUD
// src/components/visual/FlightHUD.tsx
//
// Telemetry overlay for the Starfield journey. Reads document scroll progress
// and renders phase, heading, velocity, distance, ETA, plus a center reticle
// and corner brackets. Decoupled from Starfield — drop both in independently.
//
// Usage in Home.tsx (after Starfield):
//
//   <Starfield palette="magellanic" />
//   <FlightHUD />
//
// Both are fixed-position and pointer-events-none; they don't fight content.

import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

interface PhaseInfo { code: string; sub: string; }
function phaseLabel(p: number): PhaseInfo {
  if (p < 0.10) return { code: "DEPARTURE", sub: "systems nominal" };
  if (p < 0.30) return { code: "WARP-IN",   sub: "spooling FTL drive" };
  if (p < 0.55) return { code: "CRUISE",    sub: "subspace lane open" };
  if (p < 0.80) return { code: "APPROACH",  sub: "destination acquired" };
  if (p < 0.95) return { code: "BRAKING",   sub: "realspace decel" };
  return                { code: "ARRIVAL",  sub: "pax magellanic // dock" };
}

export interface FlightHUDProps {
  /** Show the corner brackets, scan grid, and reticle. Default true. */
  chrome?: boolean;
  /** Show numerical telemetry (phase/heading/velocity/distance). Default true. */
  telemetry?: boolean;
  className?: string;
}

export function FlightHUD({ chrome = true, telemetry = true, className }: FlightHUDProps) {
  const reduced = useReducedMotion();
  const phaseRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const velRef = useRef<HTMLDivElement>(null);
  const distRef = useRef<HTMLDivElement>(null);
  const etaRef = useRef<HTMLDivElement>(null);
  const reticleRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);

  useEffect(() => {
    // Cache the page's scroll range so we don't trigger a forced reflow on
    // every scroll event by reading scrollHeight (a layout property).
    // scrollHeight only changes when the document's content height changes
    // — viewport resize or content swap (lazy images loading, etc.) — so we
    // recompute on those events instead.
    let max = 0;
    function recalc() {
      max = document.documentElement.scrollHeight - window.innerHeight;
    }
    function onScroll() {
      progressRef.current = max > 0 ? clamp01(window.scrollY / max) : 0;
    }
    recalc();
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recalc, { passive: true });
    const ro = new ResizeObserver(recalc);
    ro.observe(document.body);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recalc);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (reduced) {
      // Static readout for reduced-motion
      if (phaseRef.current) phaseRef.current.textContent = "DEPARTURE";
      if (subRef.current) subRef.current.textContent = "systems nominal";
      if (headingRef.current) headingRef.current.textContent = "042.0°";
      if (velRef.current) velRef.current.textContent = "0.04 km/s";
      if (distRef.current) distRef.current.textContent = "4.21 ly";
      if (etaRef.current) etaRef.current.textContent = "T-18:00";
      return;
    }
    let raf = 0;
    // ~24fps is ample for telemetry readouts + reticle jitter, and keeps the
    // main thread free for scrolling.
    let lastT = 0;
    const FRAME_MS = 1000 / 24;
    function loop(now: number) {
      raf = requestAnimationFrame(loop);
      if (now - lastT < FRAME_MS) return;
      lastT = now;
      const p = progressRef.current;

      let vel = 0.04;
      if (p < 0.10) vel = 0.04 + p * 0.4;
      else if (p < 0.30) vel = 0.08 + (p - 0.10) * 18;
      else if (p < 0.55) vel = 3.6 + Math.sin(now * 0.001) * 0.08;
      else if (p < 0.85) vel = 3.6 - (p - 0.55) * 11;
      else vel = Math.max(0.02, 0.6 - (p - 0.85) * 4);

      const heading = (42 + Math.sin(now * 0.0003) * 2 + p * 8).toFixed(1);
      const dist = Math.max(0, (1 - p) * 4.21).toFixed(2);
      const etaMin = Math.max(0, Math.round((1 - p) * 18));
      const etaSec = Math.max(0, Math.round((1 - p * 1.3) * 60) % 60);

      const ph = phaseLabel(p);

      if (phaseRef.current) phaseRef.current.textContent = ph.code;
      if (subRef.current) subRef.current.textContent = ph.sub;
      if (headingRef.current) headingRef.current.textContent = heading + "°";
      if (velRef.current) {
        velRef.current.textContent = vel < 0.5 ? vel.toFixed(2) + " km/s" : vel.toFixed(2) + " ly/s";
      }
      if (distRef.current) distRef.current.textContent = dist + " ly";
      if (etaRef.current) {
        etaRef.current.textContent = `T-${String(etaMin).padStart(2, "0")}:${String(etaSec).padStart(2, "0")}`;
      }
      if (reticleRef.current) {
        const j = Math.min(6, vel * 1.4);
        const jx = (Math.sin(now * 0.013) + Math.sin(now * 0.007)) * j * 0.5;
        const jy = (Math.cos(now * 0.011) + Math.sin(now * 0.005)) * j * 0.5;
        reticleRef.current.style.transform = `translate(calc(-50% + ${jx}px), calc(-50% + ${jy}px))`;
      }
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  return (
    <div
      aria-hidden="true"
      className={className}
      style={{
        position: "fixed", inset: 0, zIndex: 5, pointerEvents: "none",
        fontFamily: "ui-monospace, Menlo, Consolas, monospace",
        color: "rgba(255,180,100,0.85)",
        fontSize: 10,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
      }}
    >
      {chrome && (
        <>
          <Bracket pos="tl" />
          <Bracket pos="tr" />
          <Bracket pos="bl" />
          <Bracket pos="br" />
          <div
            ref={reticleRef}
            style={{
              position: "absolute",
              left: "50%", top: "42%",
              width: 64, height: 64,
              transform: "translate(-50%, -50%)",
              willChange: "transform",
              opacity: 0.55,
            }}
          >
            <svg viewBox="0 0 64 64" width="64" height="64" style={{ display: "block" }}>
              <circle cx="32" cy="32" r="22" fill="none" stroke="rgba(255,180,100,0.7)" strokeWidth="0.7" strokeDasharray="2 4" />
              <circle cx="32" cy="32" r="2" fill="rgba(255,200,140,0.9)" />
              <line x1="32" y1="2"  x2="32" y2="14" stroke="rgba(255,180,100,0.7)" strokeWidth="0.7" />
              <line x1="32" y1="50" x2="32" y2="62" stroke="rgba(255,180,100,0.7)" strokeWidth="0.7" />
              <line x1="2"  y1="32" x2="14" y2="32" stroke="rgba(255,180,100,0.7)" strokeWidth="0.7" />
              <line x1="50" y1="32" x2="62" y2="32" stroke="rgba(255,180,100,0.7)" strokeWidth="0.7" />
            </svg>
          </div>
          <div
            style={{
              position: "absolute", inset: 0,
              backgroundImage:
                "linear-gradient(rgba(180,200,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,165,80,1) 1px, transparent 1px)",
              backgroundSize: "120px 120px",
              opacity: 0.04,
            }}
          />
        </>
      )}

      {telemetry && (
        <>
          {/* Phase — hidden on mobile because the four telemetry corners
              overlap the Hero's centered "Boutique AI Consulting" pill on
              narrow screens. Chrome (brackets, reticle, grid) still shows. */}
          <div className="hidden md:block" style={{ position: "absolute", top: 120, left: 24 }}>
            <div style={{ color: "rgba(255,180,100,0.55)", marginBottom: 4 }}>// phase</div>
            <div ref={phaseRef} style={{ fontSize: 14, color: "rgba(255,200,140,1)", letterSpacing: "0.32em" }}>DEPARTURE</div>
            <div ref={subRef} style={{ fontSize: 9, color: "rgba(180,200,255,0.55)", marginTop: 4 }}>systems nominal</div>
          </div>
          {/* Heading */}
          <div className="hidden md:block" style={{ position: "absolute", top: 120, right: 24, textAlign: "right" }}>
            <div style={{ color: "rgba(180,200,255,0.55)", marginBottom: 4 }}>heading //</div>
            <div ref={headingRef} style={{ fontSize: 14, color: "rgba(200,220,255,1)", letterSpacing: "0.18em" }}>042.0°</div>
            <div style={{ fontSize: 9, color: "rgba(180,200,255,0.55)", marginTop: 4 }}>azimuth lock</div>
          </div>
          {/* Velocity */}
          <div className="hidden md:block" style={{ position: "absolute", bottom: 28, left: 24 }}>
            <div style={{ color: "rgba(255,180,100,0.55)", marginBottom: 4 }}>// velocity</div>
            <div ref={velRef} style={{ fontSize: 13, color: "rgba(255,200,140,1)", letterSpacing: "0.18em" }}>0.04 km/s</div>
          </div>
          {/* Distance + ETA */}
          <div className="hidden md:block" style={{ position: "absolute", bottom: 28, right: 24, textAlign: "right" }}>
            <div style={{ color: "rgba(180,200,255,0.55)", marginBottom: 4 }}>destination //</div>
            <div ref={distRef} style={{ fontSize: 13, color: "rgba(200,220,255,1)", letterSpacing: "0.18em" }}>4.21 ly</div>
            <div ref={etaRef} style={{ fontSize: 9, color: "rgba(180,200,255,0.55)", marginTop: 4 }}>T-18:00</div>
          </div>
        </>
      )}
    </div>
  );
}

function Bracket({ pos }: { pos: "tl" | "tr" | "bl" | "br" }) {
  const stroke = "rgba(255,180,100,0.55)";
  const styles: Record<string, React.CSSProperties> = {
    tl: { top: 110, left: 14 },
    tr: { top: 110, right: 14, transform: "scaleX(-1)" },
    bl: { bottom: 18, left: 14, transform: "scaleY(-1)" },
    br: { bottom: 18, right: 14, transform: "scale(-1,-1)" },
  };
  return (
    <svg width={22} height={22} viewBox="0 0 22 22" style={{ position: "absolute", ...styles[pos] }}>
      <path d="M1 8 L1 1 L8 1" fill="none" stroke={stroke} strokeWidth="1" />
    </svg>
  );
}
